/**
 * Payment requests (GH-845): Console → Payments → Requests, and the pay link a recipient follows.
 *
 *   GET  /v1/organizations/:organizationId/payment-requests              list, filters, totals
 *   GET  /v1/organizations/:organizationId/payment-requests/pledges      open pledges to link
 *   POST /v1/organizations/:organizationId/payment-requests              create (and send)
 *   POST /v1/organizations/:organizationId/payment-requests/:id/send     send, or resend
 *   POST /v1/organizations/:organizationId/payment-requests/:id/cancel
 *   POST /v1/organizations/:organizationId/payment-requests/:id/link     a fresh pay link to copy
 *   GET  /v1/public/payment-requests/:token                              what the link opens
 *
 * A request is a **request to give**, never a bill: the donor-facing words are "payment request"
 * or "pledge statement", and the gift that answers one is an ordinary donation with the ordinary
 * receipt. Amounts are integer minor units as strings, like every other money field here.
 *
 * The status list is spelled out rather than imported from `@givehere/domain`, which this package
 * keeps out of the browser bundles; `services/api` pins the two lists equal in a test.
 */
import { z } from "zod";
import { minorUnitsSchema, moneySchema } from "./v1.js";
export const PAYMENT_REQUEST_STATUSES = [
    "draft",
    "sent",
    "viewed",
    "partly_paid",
    "paid",
    "cancelled",
    "expired",
];
/** `open` is the filter for sent, viewed and partly paid together: the ones still being asked. */
export const PAYMENT_REQUEST_FILTERS = ["all", "open", ...PAYMENT_REQUEST_STATUSES];
/**
 * How a request reaches its recipient. `postal` is the seam GH-846 builds on: accepted by the
 * contract, refused by the server while `FEATURE_POSTAL_PAYMENT_REQUESTS` is off.
 */
export const PAYMENT_REQUEST_CHANNELS = ["email", "postal"];
export const PAYMENT_REQUEST_NOTE_MAX = 500;
export const PAYMENT_REQUEST_NAME_MAX = 120;
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "a date is YYYY-MM-DD")
    .refine((value) => !Number.isNaN(Date.parse(`${value}T12:00:00Z`)), "not a real date");
/** A US mailing address. Stored only for a postal request; never shown back in full. */
export const requestMailingAddressSchema = z
    .object({
    line1: z.string().trim().min(1).max(120),
    line2: z.string().trim().max(120).optional(),
    city: z.string().trim().min(1).max(80),
    region: z.string().trim().min(2).max(40),
    postalCode: z
        .string()
        .trim()
        .regex(/^\d{5}(-\d{4})?$/, "a ZIP code is five digits, or five and four"),
    country: z.literal("US").default("US"),
})
    .strict();
/**
 * `POST …/payment-requests`. The amount is checked again on the server against the same $1 floor
 * and $100,000 ceiling as a gift. With a `pledgeId` and no email, the request goes to the address
 * the pledge was made with — resolved on the server and never sent to the console.
 */
export const createPaymentRequestSchema = z
    .object({
    recipientName: z.string().trim().min(1).max(PAYMENT_REQUEST_NAME_MAX),
    recipientEmail: z.string().trim().toLowerCase().email().max(254).optional(),
    postalAddress: requestMailingAddressSchema.optional(),
    amountMinor: minorUnitsSchema,
    designationId: z.string().uuid().nullable().optional(),
    pledgeId: z.string().uuid().optional(),
    note: z.string().trim().max(PAYMENT_REQUEST_NOTE_MAX).optional(),
    dueOn: isoDate.optional(),
    deliveryChannel: z.enum(PAYMENT_REQUEST_CHANNELS).default("email"),
    /** Send at once (the default), or keep a draft to send later. */
    sendNow: z.boolean().default(true),
})
    .strict()
    .superRefine((value, context) => {
    if (value.deliveryChannel === "email" && !value.recipientEmail && !value.pledgeId) {
        context.addIssue({
            code: "custom",
            path: ["recipientEmail"],
            message: "An email request needs an email address.",
        });
    }
    if (value.deliveryChannel === "postal" && !value.postalAddress) {
        context.addIssue({
            code: "custom",
            path: ["postalAddress"],
            message: "A printed letter needs a mailing address.",
        });
    }
    if (value.pledgeId && value.designationId) {
        context.addIssue({
            code: "custom",
            path: ["designationId"],
            message: "A pledge keeps the purpose it was made for.",
        });
    }
});
export const paymentRequestListQuerySchema = z
    .object({
    status: z.enum(PAYMENT_REQUEST_FILTERS).default("all"),
    /** A recipient name, matched case-insensitively. The organization's own records. */
    q: z.string().trim().max(120).optional(),
})
    .strict();
/** Which actions the server will accept on this row now, for this caller. Never inferred. */
export const paymentRequestActionsSchema = z.object({
    send: z.boolean(),
    resend: z.boolean(),
    /** "Remind now" (GH-849): an open emailed request with something still asked. */
    remind: z.boolean().default(false),
    cancel: z.boolean(),
    copyLink: z.boolean(),
});
/** What Lob last said about a letter (GH-849), in our words. */
export const PAYMENT_REQUEST_LETTER_STATUSES = [
    "queued",
    "mailed",
    "in_transit",
    "in_local_area",
    "processed_for_delivery",
    "delivered",
    "re_routed",
    "returned_to_sender",
    "cancelled",
    "failed",
];
export const paymentRequestLetterSchema = z.object({
    status: z.enum(PAYMENT_REQUEST_LETTER_STATUSES),
    /** The letter came back. Shown to staff; nothing is reversed or cancelled automatically. */
    returnedToSender: z.boolean(),
    deliveredAt: z.string().nullable(),
    returnedToSenderAt: z.string().nullable(),
    lastEventAt: z.string().nullable(),
});
export const paymentRequestRowSchema = z.object({
    id: z.string().uuid(),
    status: z.enum(PAYMENT_REQUEST_STATUSES),
    deliveryChannel: z.enum(PAYMENT_REQUEST_CHANNELS),
    recipientName: z.string(),
    /** "a•••@example.org" — the console never receives the address itself. */
    recipientEmailMasked: z.string().nullable(),
    hasPostalAddress: z.boolean(),
    amount: moneySchema,
    paid: moneySchema,
    outstanding: moneySchema,
    designationId: z.string().uuid().nullable(),
    designationName: z.string().nullable(),
    pledgeId: z.string().uuid().nullable(),
    eventName: z.string(),
    note: z.string().nullable(),
    dueOn: z.string().nullable(),
    createdAt: z.string(),
    createdByName: z.string().nullable(),
    sentAt: z.string().nullable(),
    lastSentAt: z.string().nullable(),
    sendCount: z.number().int().nonnegative(),
    firstViewedAt: z.string().nullable(),
    paidAt: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    expiresAt: z.string().nullable(),
    /** Reminder emails that went (GH-849), scheduled or "Remind now". */
    reminders: z
        .object({ count: z.number().int().nonnegative(), lastAt: z.string().nullable() })
        .default({ count: 0, lastAt: null }),
    /** The newest letter for a postal request, as the provider last reported it. */
    letter: paymentRequestLetterSchema.nullable().default(null),
    actions: paymentRequestActionsSchema,
});
export const paymentRequestListResponseSchema = z.object({
    requests: z.array(paymentRequestRowSchema),
    /** Over every request the filter matches, not just the rows returned. */
    totals: z.object({
        requested: moneySchema,
        paid: moneySchema,
        outstanding: moneySchema,
        count: z.number().int().nonnegative(),
    }),
    /** True when more matched than were returned (the newest are returned). */
    truncated: z.boolean(),
    /** Whether this caller may create and send (`payment_request.send`). */
    canSend: z.boolean(),
    /** `FEATURE_POSTAL_PAYMENT_REQUESTS`: whether a printed letter may be chosen at all. */
    postalEnabled: z.boolean(),
});
/** An open pledge a request may be linked to, with its balance to prefill. */
export const paymentRequestPledgeOptionSchema = z.object({
    id: z.string().uuid(),
    eventName: z.string(),
    amount: moneySchema,
    outstanding: moneySchema,
    pledgedAt: z.string(),
    /** How the pledge is recognised publicly — "Amina R." or "Anonymous". Never an address. */
    donorLabel: z.string(),
    /** Whether the pledge was made with an email the request can go to without typing one. */
    hasEmail: z.boolean(),
});
export const paymentRequestPledgeOptionsResponseSchema = z.object({
    pledges: z.array(paymentRequestPledgeOptionSchema),
});
export const PAYMENT_REQUEST_DELIVERY_STATUSES = [
    "queued",
    "sent",
    "delivered",
    "failed",
    "suppressed",
    "not_sent",
];
/** The answer to create, send and resend: the row as it now stands, and what the sender said. */
export const paymentRequestMutationResponseSchema = z.object({
    request: paymentRequestRowSchema,
    delivery: z
        .object({
        status: z.enum(PAYMENT_REQUEST_DELIVERY_STATUSES),
        /** Plain words for the console: why an email did not go, never the address. */
        message: z.string(),
    })
        .nullable(),
});
export const paymentRequestLinkResponseSchema = z.object({
    url: z.string().url(),
});
/* ------------------------------------------------------- history and reminders */
export const PAYMENT_REQUEST_HISTORY_KINDS = [
    "created",
    "sent",
    "resent",
    "viewed",
    "cancelled",
    "gift_applied",
    "gift_reversed",
    "gift_ignored",
    "gift_refunded",
    "reminded",
    "letter_updated",
    // GH-853: a lost chargeback that reopened the request (by `amount`).
    "dispute_lost",
    // GH-855: that loss later won on appeal, restoring `amount` (zero: a history line only).
    "dispute_won",
];
/**
 * `GET …/payment-requests/:id/history` (GH-849) — one request's trail, oldest first. Amounts and the
 * staff member who acted; never the recipient's name or address. `gift_refunded` is a refund that
 * reopened the request (by `amount`), `dispute_lost` a lost chargeback that did (GH-853), `dispute_won` that loss won on appeal and
 * the credit it restored (GH-855; zero on a cancelled or expired request); `reminded` with `reminderDaysBefore` is a scheduled reminder
 * and without it a "Remind now"; `letter_updated` is a letter's delivery status from Lob.
 */
export const paymentRequestHistoryEntrySchema = z.object({
    kind: z.enum(PAYMENT_REQUEST_HISTORY_KINDS),
    at: z.string(),
    amount: moneySchema.nullable(),
    deliveryStatus: z.string().nullable(),
    deliveryChannel: z.enum(PAYMENT_REQUEST_CHANNELS).nullable(),
    reminderDaysBefore: z.number().int().nullable(),
    letterStatus: z.enum(PAYMENT_REQUEST_LETTER_STATUSES).nullable(),
    actorName: z.string().nullable(),
});
export const paymentRequestHistoryResponseSchema = z.object({
    entries: z.array(paymentRequestHistoryEntrySchema),
});
/**
 * The platform's reminder schedule (GH-849): whole days before a request's due date, e.g. [7, 1].
 * `GET /v1/admin/payment-request-reminders`, `POST` with a reason — platform staff only.
 */
export const paymentRequestReminderScheduleSchema = z.object({
    daysBefore: z.array(z.number().int()),
});
export const updatePaymentRequestReminderScheduleSchema = z
    .object({
    daysBefore: z.array(z.number().int().min(1).max(60)).max(4),
    reason: z.string().trim().min(3).max(500),
})
    .strict();
/* -------------------------------------------------------------- the pay link */
const publicOrganizationSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
});
/**
 * What `/r/<token>` shows. `open` carries a freshly signed checkout context for the right event,
 * designation and amount; the amount itself is still the server's at quote time. `paid` and
 * `inactive` carry no context — a finished or withdrawn request is not payable *as a request*,
 * and the page offers the organization's own page instead.
 */
export const publicPaymentRequestResponseSchema = z.discriminatedUnion("state", [
    z.object({
        state: z.literal("open"),
        organization: publicOrganizationSchema,
        /** What is still asked: the request's amount less what has already arrived. */
        amount: moneySchema,
        requested: moneySchema,
        designationName: z.string().nullable(),
        note: z.string().nullable(),
        dueOn: z.string().nullable(),
        isPledgeStatement: z.boolean(),
        contextToken: z.string(),
    }),
    z.object({
        state: z.literal("paid"),
        organization: publicOrganizationSchema,
    }),
    z.object({
        state: z.literal("inactive"),
        // `unavailable`: the organization cannot take a gift right now (payments paused or archived).
        reason: z.enum(["cancelled", "expired", "draft", "unavailable"]),
        organization: publicOrganizationSchema,
        /** Whether the organization can take a gift right now, so the page can offer one. */
        canGive: z.boolean(),
    }),
]);
//# sourceMappingURL=payment-requests.js.map